import { useEffect, useMemo } from 'react'
import { atom, getDefaultStore, useAtomValue, useSetAtom } from 'jotai'

/**
 * What a meeting is known by. All three name the same meeting; which ones are
 * known depends on when you ask — a Custom Meeting has only a session until it
 * saves, a scheduled one has an event before it records.
 */
export interface MeetingIds {
  audioNoteId?: string
  audioSessionId?: string
  calendarEventId?: string
}

const ID_KEYS = ['audioNoteId', 'audioSessionId', 'calendarEventId'] as const

/**
 * Every id a meeting is known by, each pointing at the full set. Renderer-local
 * and session-lived: a few small objects per meeting opened, which is bounded by
 * what the user can look at before the app restarts.
 *
 * This exists for the audio session, the one id that can't be re-derived: main
 * nulls it the moment the note saves, and nothing else remembers which session
 * a note came from. Filing it under the note as soon as both are known keeps it
 * reachable past the save and on a later reopen — see `meetingNotepadKey` and
 * the follow-ups that reference a session rather than a note.
 *
 * Not identity: which *entry* is showing which meeting stays a function of that
 * entry's open context (`viewIdentity`), converged by the latch in
 * `useMeetingContextLatch`. Resolving identity through here instead would make
 * it depend on when a background query landed, and let two entries silently
 * merge — collapsing the trail between them — the moment one did.
 */
const meetingIndexAtom = atom<Record<string, MeetingIds>>({})

const knownIds = (ids: MeetingIds): Array<string> => ID_KEYS.map((key) => ids[key]).filter((id): id is string => !!id)

const sameIds = (a: MeetingIds, b: MeetingIds): boolean => ID_KEYS.every((key) => a[key] === b[key])

/** Everything known about the meeting `ids` names — the given ids win, the index fills the rest. */
export function resolveMeetingIds(index: Record<string, MeetingIds>, ids: MeetingIds): MeetingIds {
  const resolved: MeetingIds = { ...ids }
  for (const id of knownIds(ids)) {
    const known = index[id]
    if (!known) continue
    for (const key of ID_KEYS) {
      resolved[key] ??= known[key]
    }
  }
  return resolved
}

/**
 * Fold a newly-resolved set into the index, under every id it names — so a
 * meeting first seen as a bare session is reachable by its note id the moment
 * it saves. Returns the same index when it learned nothing, which is what keeps
 * the recording effect from looping.
 */
export function recordMeetingIds(index: Record<string, MeetingIds>, ids: MeetingIds): Record<string, MeetingIds> {
  const resolved = resolveMeetingIds(index, ids)
  const keys = knownIds(resolved)
  if (!keys.length || keys.every((id) => index[id] && sameIds(index[id], resolved))) {
    return index
  }
  const next = { ...index }
  for (const id of keys) {
    next[id] = resolved
  }
  return next
}

/**
 * Imperative resolve against the session index, for open-time context
 * enrichment (`normalize` in the view registry). One-shot at the moment of
 * intent — unlike resolving `viewIdentity` through the index, this can't merge
 * two already-open entries when a background query lands later.
 */
export function getResolvedMeetingIds(ids: MeetingIds): MeetingIds {
  return resolveMeetingIds(getDefaultStore().get(meetingIndexAtom), ids)
}

/** Publish what a view resolved about its meeting. Idempotent — repeat writes of a known set are a no-op. */
export function useRecordMeetingIds(ids: MeetingIds): void {
  const setIndex = useSetAtom(meetingIndexAtom)
  const { audioNoteId, audioSessionId, calendarEventId } = ids

  useEffect(() => {
    setIndex((index) => recordMeetingIds(index, { audioNoteId, audioSessionId, calendarEventId }))
  }, [audioNoteId, audioSessionId, calendarEventId, setIndex])
}

/** Everything known about the meeting `ids` names, reactively. */
export function useResolvedMeetingIds(ids: MeetingIds): MeetingIds {
  const index = useAtomValue(meetingIndexAtom)
  const { audioNoteId, audioSessionId, calendarEventId } = ids

  return useMemo(
    () => resolveMeetingIds(index, { audioNoteId, audioSessionId, calendarEventId }),
    [index, audioNoteId, audioSessionId, calendarEventId],
  )
}
